import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { eligibilitiesApi } from '../../eligibilities/eligibilitiesApi';
import type { EligibilityOption } from '../../eligibilities/eligibilityTypes';
import { salaryGradesApi } from '../../plantilla/plantillaApi';
import {
  salaryTypeLabel,
  type SalaryGradeOption,
  type SalaryTypeValue,
} from '../../plantilla/plantillaTypes';
import { positionsApi } from '../positionsApi';
import type { PositionListItem } from '../positionTypes';
import { samplePosition } from '../utils/samplePosition';

/** The value each picker uses for "this title carries none". */
const NO_GRADE = '__none__';
const NO_ELIGIBILITY = '__none__';

/**
 * Create or edit one catalog title, qualification standards included.
 *
 * The QS lives here rather than on a plantilla post because it is a property of the
 * designation: two posts drawn from the same title demand the same education, and recording
 * that twice is two rows to keep in step and one to forget.
 */
export function PositionModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: PositionListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [title, setTitle] = useState(editing?.title ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [gradeKey, setGradeKey] = useState<string>(
    editing?.salaryGrade == null
      ? NO_GRADE
      : `${editing.salaryGradeType ?? 0}:${editing.salaryGrade}:${editing.salaryStep ?? 1}`,
  );
  const [description, setDescription] = useState(editing?.description ?? '');

  /*
    The three documents are UNCONTROLLED after mount — the editor owns the value and reports
    upward. Pushing state back in on every render resets the caret to the start of the
    document on every keystroke, which is why Autogenerate leaves them alone.
  */
  const [education, setEducation] = useState(editing?.education ?? '');
  const [training, setTraining] = useState(editing?.training ?? '');
  const [experience, setExperience] = useState(editing?.experience ?? '');
  const [eligibilityId, setEligibilityId] = useState<string>(editing?.eligibilityId ?? NO_ELIGIBILITY);

  const [grades, setGrades] = useState<SalaryGradeOption[]>([]);
  const [eligibilities, setEligibilities] = useState<EligibilityOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      salaryGradesApi.options(controller.signal),
      eligibilitiesApi.options(controller.signal),
    ])
      .then(([loadedGrades, loadedEligibilities]) => {
        setGrades(loadedGrades);
        setEligibilities(loadedEligibilities);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load the pickers.', 0);

      setError(failure);
      // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingOptions(false);
      });

    return () => controller.abort();
  }, []);

  const codeValid = /^[A-Za-z0-9-]+$/.test(code.trim());
  const valid =
    title.trim().length > 0 && code.trim().length > 0 && codeValid && gradeKey !== NO_GRADE;
  const canSave = valid && confirmed && !saving;

  /*
    A grade the schedule no longer offers stays selectable where it already was — the title
    still records it, and a picker that dropped it would read as blank on a row that has one.
  */
  const gradeOptions = grades.map((g) => ({
    value: `${g.type}:${g.grade}:${g.step}`,
    label: `SG ${g.grade}-${g.step}`,
    right: salaryTypeLabel(g.type),
  }));

  const editingGradeMissing =
    gradeKey !== NO_GRADE && !gradeOptions.some((option) => option.value === gradeKey);

  if (editingGradeMissing && editing) {
    gradeOptions.push({
      value: gradeKey,
      label: `SG ${editing.salaryGrade}-${editing.salaryStep}`,
      right: `${salaryTypeLabel(editing.salaryGradeType)} · no longer listed`,
    });
  }

  /*
    The grade is required and its only source is the published schedule, so an empty schedule
    means no title can be created at all. The form says that outright rather than showing a
    required picker with nothing in it — and Autogenerate is withheld below for the same
    reason: a button that cannot produce a saveable form is worse than no button.
  */
  const scheduleEmpty = !loadingOptions && gradeOptions.length === 0;

  const eligibilityOptions = eligibilities.map((e) => ({ value: e.id, label: e.name }));

  /*
    A deleted eligibility a title already requires stays selectable and SAYS it is deleted,
    rather than vanishing and leaving the field looking empty on a record that has one. The
    server refuses it on a fresh write, which is what makes saying so honest rather than a
    trap: the reader can see what is recorded and is told to pick a live one.
  */
  if (
    eligibilityId !== NO_ELIGIBILITY &&
    !eligibilityOptions.some((option) => option.value === eligibilityId) &&
    editing?.eligibilityName
  ) {
    eligibilityOptions.push({ value: eligibilityId, label: `${editing.eligibilityName} · deleted` });
  }

  /**
   * Fills all three required fields — title, code and grade.
   *
   * The grade is resolved against the published schedule rather than invented: a pair the
   * picker does not offer would leave the field looking untouched, and it is required now, so
   * a generated form without one would not save. The three documents are left empty — an
   * uncontrolled editor owns its value after mount, so setting the state behind it would leave
   * the form and the screen disagreeing.
   */
  function autogenerate() {
    const sample = samplePosition();
    setTitle(sample.title);
    setCode(sample.code);
    setGradeKey(gradeOptions[Math.floor(Math.random() * gradeOptions.length)].value);
  }

  /** Only ever called once `valid` holds, so the grade is never the placeholder. */
  function splitGrade() {
    const [gradeType, grade, step] = gradeKey.split(':').map(Number);
    return {
      salaryGrade: grade,
      salaryGradeType: gradeType as SalaryTypeValue,
      salaryStep: step,
    };
  }

  /**
   * Adds an eligibility from inside the picker, then selects it.
   *
   * The picker is over a foreign key, so committing the typed text — which is what
   * `allowCustom` alone does — would put a label where an id belongs. Creating the row first
   * and selecting the id it comes back with is the only version that saves.
   */
  async function createEligibility(name: string) {
    const created = await eligibilitiesApi.create({
      name,
      description: null,

      // Created from the position form, which has no business deciding whether an eligibility
      // entails a licence — that is the catalog's own question, answered in its own modal.
      entailsLicense: false,
    });
    setEligibilities((current) => [
      ...current,
      // `entailsLicense` defaults false for a newly created row; an administrator turns it on
      // in the eligibility catalog, which is the only place that decision belongs.
      { id: created.id, name: created.name, entailsLicense: false },
    ]);
    setEligibilityId(created.id);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      title: title.trim(),
      code: code.trim(),
      description: description.trim() || null,
      // An empty document is `<p></p>`, not an empty string — the server collapses both to
      // null so a standard that was never written reads as absent to every check downstream.
      education: education.trim() || null,
      training: training.trim() || null,
      experience: experience.trim() || null,
      eligibilityId: eligibilityId === NO_ELIGIBILITY ? null : eligibilityId,
      ...splitGrade(),
    };

    try {
      if (editing) await positionsApi.update(editing.id, request);
      else await positionsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the position.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, {
        idPrefix: 'position',
        /*
          The three salary fields share one control — the picker sets grade, step and
          schedule together — so a rejection of any of them points at the same place.
        */
        fieldIds: {
          salaryGrade: 'position-grade',
          salaryStep: 'position-grade',
          salaryGradeType: 'position-grade',
          eligibilityId: 'position-eligibility',
        },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit position' : 'New position'}
      // Three document editors and two-column rows; `md` would clip both.
      size="2xl"
      id="position-modal"
      // Create only. On a form over existing data it would overwrite the record's real values.
      /*
        Create only — on a form over existing data it would overwrite the record's real
        values. Withheld entirely while the schedule is empty: the grade is required and its
        only source is that schedule, so the button could not produce a saveable form, and no
        button is better than one that leaves the primary action disabled.
      */
      autogenerate={
        editing === null && !scheduleEmpty
          ? { onGenerate: autogenerate, disabled: saving || loadingOptions }
          : undefined
      }
      confirm={{
        id: 'position-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="position-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="position-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="position-title"
          label="Title"
          required
          description="The designation, e.g. Administrative Officer II. Held once in the catalog, however many posts carry it."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={150}
          disabled={saving}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            id="position-code"
            label="Code"
            required
            /*
              Editable on an edit, unlike a post's code — nothing is named after this one. A
              post numbers its slots from its OWN code and points at this row by id, so
              correcting a typo here strands nothing.
            */
            description="A short identifier for the title, e.g. ADOF2. Nothing is numbered from it, so it can be corrected later."
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={20}
            error={code === '' || codeValid ? undefined : 'Letters, numbers and hyphens only.'}
            disabled={saving}
          />

          <Select
            id="position-grade"
            label="Salary grade"
            required
            remote
            loading={loadingOptions}
            clearable={false}
            description="The grade this title is authorized at. Managed under Salary Grades, and every post drawn from this title reads its grade from here."
            value={gradeKey}
            onChange={(next) => setGradeKey(next ?? NO_GRADE)}
            /*
              No "None" option any more: the grade is required, so offering one would put a
              choice on the form that the primary action then refuses. The placeholder value
              is unselectable — it only ever holds the pre-choice state.
            */
            options={gradeOptions}
            error={
              scheduleEmpty
                ? 'No salary grade has been published yet. Add one under Salary Grades first.'
                : gradeKey === NO_GRADE
                  ? 'Choose the grade this title is authorized at.'
                  : undefined
            }
            disabled={saving || scheduleEmpty}
          />
        </div>

        <TextField
          id="position-description"
          label="Description"
          description="Optional note shown on the position."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />

        {/*
          The qualification standards. Prose, so they are documents — a single-line field made
          every one of them a run-on sentence. All optional: a title can be catalogued before
          its QS is written up.
        */}
        <div className="border-t border-slate-200 pt-4">
          <h3 className="text-sm text-slate-800">Qualification standards</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            What this title demands of whoever holds it. Every field is optional, and a post
            drawn from this title inherits all of them.
          </p>
        </div>

        <Select
          id="position-eligibility"
          label="Eligibility"
          remote
          loading={loadingOptions}
          clearable={false}
          searchable
          /*
            A controlled value, unlike the three documents beside it: the same eligibility is
            named by many titles, and a typed one would quietly become a second eligibility
            differing by a comma. `allowCustom` with a create form rather than a bare
            `allowCustom`, because this picker is over an id — see createEligibility.
          */
          allowCustom
          onCreateOption={(raw) => createEligibility(raw)}
          description="The eligibility this title requires. Type a new one to add it to the catalog."
          value={eligibilityId}
          onChange={(next) => setEligibilityId(next ?? NO_ELIGIBILITY)}
          options={[
            { value: NO_ELIGIBILITY, label: 'None — this title requires no eligibility' },
            ...eligibilityOptions,
          ]}
          disabled={saving}
        />

        {/*
          Loaded on demand: the editor is larger than the rest of this application, and three
          of them on one form is the clearest possible reason not to put it in the main
          bundle. <LazyRichTextField> owns the split and a fallback in the field's own shape.
        */}
        <LazyRichTextField
          id="position-education"
          label="Education"
          description="The education this title requires, e.g. a bachelor's degree relevant to the job."
          value={education}
          onChange={setEducation}
          disabled={saving}
        />

        <LazyRichTextField
          id="position-training"
          label="Training"
          description="The training this title requires, in hours or by name."
          value={training}
          onChange={setTraining}
          disabled={saving}
        />

        <LazyRichTextField
          id="position-experience"
          label="Experience"
          description="The experience this title requires, in years or by kind of work."
          value={experience}
          onChange={setExperience}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
